import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USER_STATUS_LABELS, type InvitationResult, type RoleSummary, type UserList, type UserStatus, type UserSummary } from '@kontor/shared';
import { api, ApiError } from '../api';
import { FilterBar, type FilterField } from '../components/filter/FilterBar';
import { InvitationLink } from '../components/InvitationLink';
import { SeatsBookDialog } from '../components/SeatsBookDialog';
import { useSession } from '../session';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

function StatusLabel({ user }: { user: UserSummary }) {
  const expired = user.status === 'invited' && user.invitationExpiresAt !== null && new Date(user.invitationExpiresAt).getTime() < Date.now();
  const tone = user.status === 'active' ? 'badge--won' : user.status === 'invited' ? 'badge--in_progress' : '';
  return (
    <>
      <span className={`badge ${tone}`}>{USER_STATUS_LABELS[user.status]}</span>
      {expired && <div className="muted small">Link abgelaufen</div>}
    </>
  );
}

/** Kleinschreibung ohne Akzente, damit „mueller“ auch „Müller“ findet */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').toLowerCase();

/** Bereich „Benutzer“ der Einrichtung, mit Filter nach Rolle und Status (KON-074). Die Rechte prüft der Server, die Oberfläche blendet nur aus. */
export function UsersPage() {
  const { can, session } = useSession();
  const client = useQueryClient();
  const canWrite = can('users.write');
  const canBuy = can('setup.billing');
  const [buying, setBuying] = useState(false);
  const [invitation, setInvitation] = useState<InvitationResult | null>(null);
  const [mailRequested, setMailRequested] = useState(false);
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const roleFilter = params.get('role') ?? '';
  const statusFilter = params.get('status') ?? '';

  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserList>('GET', '/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => (await api<{ data: RoleSummary[] }>('GET', '/users/roles')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['users'] });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api('PATCH', `/users/${id}`, { isActive }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/users/${id}`),
    onSuccess: refresh,
  });
  const renew = useMutation({
    // Ist der Mailversand eingerichtet, geht der neue Link zusätzlich per E-Mail raus
    mutationFn: (id: string) => api<{ data: InvitationResult }>('POST', `/users/${id}/invitation`, { sendMail: users.data?.meta.mailEnabled ?? false }),
    onSuccess: (res) => {
      setMailRequested(users.data?.meta.mailEnabled ?? false);
      setInvitation(res.data);
      refresh();
    },
  });

  const error = [setActive.error, renew.error, remove.error, users.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const meta = users.data?.meta;
  const words = fold(q).split(/\s+/).filter(Boolean);
  const shown = (users.data?.data ?? []).filter(
    (u) => (!roleFilter || u.role.key === roleFilter || u.extraRoles.some((r) => r.key === roleFilter)) && (!statusFilter || u.status === statusFilter) && words.every((w) => fold(`${u.name} ${u.email}`).includes(w)),
  );
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'role', label: 'Rolle', type: 'select', options: [{ value: '', label: 'Alle Rollen' }, ...(roles.data ?? []).map((r) => ({ value: r.key, label: r.name }))] },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...(Object.keys(USER_STATUS_LABELS) as UserStatus[]).map((s) => ({ value: s, label: USER_STATUS_LABELS[s] }))] },
  ];
  const full = meta ? meta.seatsUsed >= meta.maxUsers : false;

  return (
    <>
      {invitation && <InvitationLink result={invitation} mailRequested={mailRequested} onClose={() => setInvitation(null)} />}

      {buying && <SeatsBookDialog onClose={() => setBuying(false)} />}

      <section className="card">
        <div className="page-head">
          <h2>Benutzer</h2>
          {canBuy && <button type="button" className="btn btn--primary btn--sm page-head__action" onClick={() => setBuying(true)}>Benutzerlizenzen kaufen</button>}
        </div>
        <p className="muted small">
          {meta ? `${meta.seatsUsed} von ${meta.maxUsers} Plätzen Ihres Pakets sind belegt. ` : ''}
          Aktive und eingeladene Benutzer belegen einen Platz, deaktivierte nicht.
          {full && (canBuy ? ' Deaktivieren Sie einen Benutzer oder kaufen Sie Benutzerlizenzen.' : ' Deaktivieren Sie einen Benutzer, oder bitten Sie den Inhaber, Benutzerlizenzen zu kaufen.')}
        </p>

        <FilterBar
          search={{ value: q, placeholder: 'Name oder E-Mail suchen', onSubmit: (v) => update({ q: v }) }}
          fields={fields}
          values={{ role: roleFilter, status: statusFilter }}
          onChange={(key, value) => update({ [key]: value })}
          onReset={() => setParams(new URLSearchParams())}
        />

        {error && <div className="alert alert--error" role="alert">{error.message}</div>}

        <table className="table table--cards">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rolle</th>
              <th>Status</th>
              <th>Letzte Anmeldung</th>
              <th><span className="sr-only">Aktionen</span></th>
            </tr>
          </thead>
          <tbody>
            {users.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
            {users.data && shown.length === 0 && <tr><td colSpan={5} className="empty">Kein Benutzer passt zu den Filtern.</td></tr>}
            {shown.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.name}
                  <div className="muted small">{u.email}</div>
                </td>
                <td data-label="Rolle">{[u.role.name, ...u.extraRoles.map((r) => r.name)].join(' + ')}</td>
                <td data-label="Status"><div><StatusLabel user={u} /></div></td>
                <td data-label="Letzte Anmeldung" className="nowrap">{u.lastLoginAt ? dateFormat.format(new Date(u.lastLoginAt)) : '–'}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                  {canWrite && (
                    <>
                      <Link className="btn btn--secondary btn--sm" to={`/einrichtung/benutzer/${u.id}`}>Bearbeiten</Link>
                      {u.status === 'invited' && (
                        <button type="button" className="btn btn--secondary btn--sm" disabled={renew.isPending} onClick={() => renew.mutate(u.id)}>Neuer Link</button>
                      )}
                      {u.status === 'deactivated' && (
                        <button type="button" className="btn btn--secondary btn--sm" disabled={setActive.isPending} onClick={() => setActive.mutate({ id: u.id, isActive: true })}>Aktivieren</button>
                      )}
                      {can('users.delete') && u.id !== session?.user.id && (
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`${u.name} endgültig löschen?

Der Zugang wird entfernt und die E-Mail-Adresse ${u.email} wird wieder frei. Zugeordnete Anfragen bleiben bestehen, haben danach aber keinen Zuständigen. Das lässt sich nicht rückgängig machen. Wer nur gesperrt werden soll, wird stattdessen deaktiviert.`)) remove.mutate(u.id);
                          }}
                        >
                          Löschen
                        </button>
                      )}
                      {u.status !== 'deactivated' && u.id !== session?.user.id && (
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          disabled={setActive.isPending}
                          onClick={() => {
                            if (window.confirm(`${u.name} wirklich deaktivieren? Offene Anmeldungen enden sofort.`)) setActive.mutate({ id: u.id, isActive: false });
                          }}
                        >
                          Deaktivieren
                        </button>
                      )}
                    </>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {canWrite && (
          <div className="form-actions form-actions--end">
            {full ? (
              <button type="button" className="btn btn--primary" disabled title="Alle Plätze Ihres Pakets sind belegt.">Benutzer einladen</button>
            ) : (
              <Link className="btn btn--primary" to="/einrichtung/benutzer/neu">Benutzer einladen</Link>
            )}
          </div>
        )}
      </section>
    </>
  );
}
